import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { 
  Bell, 
  Calendar, 
  Clock, 
  CheckCircle,
  Star,
  Users,
  Zap
} from 'lucide-react';
import { IntegrationDefinition } from '../../../integrations/types';
import { IntegrationIcon } from './IntegrationIcon';
import MemoryTypesBadges from '../../ui/MemoryTypesBadges';

interface ComingSoonModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: IntegrationDefinition;
}

export default function ComingSoonModal({ isOpen, onClose, integration }: ComingSoonModalProps) {
  const { t } = useTranslation('integrations');
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleNotifySignup = async () => {
    setIsLoading(true);
    // TODO: Implement actual email signup
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
    }, 1000);
  };

  const getStatusInfo = () => {
    switch (integration.status) {
      case 'release_1_4':
        return {
          title: 'Scheduled for Release 1.4',
          description: 'This integration is actively being developed and will be available in Release 1.4',
          timeline: '10-14 weeks',
          icon: Calendar,
          color: 'purple'
        };
      case 'in_progress':
        return {
          title: 'Currently in Development',
          description: 'Our team is actively working on this integration',
          timeline: '6-8 weeks',
          icon: Zap,
          color: 'yellow'
        };
      case 'coming_soon':
        return {
          title: 'Coming Soon',
          description: 'This integration is planned for a future release',
          timeline: '3-6 months',
          icon: Clock,
          color: 'orange'
        };
      case 'concept':
        return {
          title: 'Concept Phase',
          description: 'This integration is in the concept and planning phase',
          timeline: '6+ months',
          icon: Star,
          color: 'gray'
        };
      default:
        return {
          title: 'Coming Soon',
          description: 'This integration will be available in the future',
          timeline: 'TBD',
          icon: Clock,
          color: 'gray'
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center space-x-3 mb-2">
            <div className={`w-12 h-12 bg-gradient-to-r ${integration.gradient} rounded-lg flex items-center justify-center`}>
              <IntegrationIcon icon={integration.icon} className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl">{integration.name}</DialogTitle>
              <Badge 
                variant="secondary" 
                className={`mt-1 ${
                  statusInfo.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                  statusInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                  statusInfo.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                  'bg-gray-100 text-gray-800'
                }`}
              >
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusInfo.title}
              </Badge>
            </div>
          </div>
          <DialogDescription className="text-base">
            {integration.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 -mt-1 mb-2">
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {t('memoryModalities.headingSelection')}
          </h4>
          <MemoryTypesBadges integrationId={integration.id} compact className="gap-1" />
        </div>

        <div className="space-y-6">
          {/* Status Information */}
          <div className={`p-4 rounded-lg border ${
            statusInfo.color === 'purple' ? 'bg-purple-50 border-purple-200' :
            statusInfo.color === 'yellow' ? 'bg-yellow-50 border-yellow-200' :
            statusInfo.color === 'orange' ? 'bg-orange-50 border-orange-200' :
            'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Development Status</h4>
              <span className="text-sm font-medium">~{statusInfo.timeline}</span>
            </div>
            <p className="text-sm text-gray-600">{statusInfo.description}</p>
          </div>

          {/* Feature Preview */}
          {integration.setupSteps && (
            <div>
              <h4 className="font-medium mb-3">What you'll be able to do:</h4>
              <ul className="space-y-2">
                {integration.setupSteps.map((step: string, index: number) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Social Proof */}
          <div className={`p-3 rounded-lg bg-blue-50 border border-blue-200`}>
            <div className="flex items-center space-x-2 text-blue-800">
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">
                Join 1,200+ users waiting for this integration
              </span>
            </div>
          </div>

          {/* Email Signup */}
          {!isSubscribed ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Bell className="w-4 h-4 text-gray-500" />
                <span className="font-medium">Get notified when it's ready</span>
              </div>
              <div className="flex space-x-2">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={handleNotifySignup}
                  disabled={!email || isLoading}
                  className={`bg-gradient-to-r ${integration.gradient} text-white border-0`}
                >
                  {isLoading ? 'Subscribing...' : 'Notify Me'}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                We'll send you one email when this integration is ready. No spam.
              </p>
            </div>
          ) : (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2 text-green-800">
                <CheckCircle className="w-5 h-5" />
                <div>
                  <p className="font-medium">You're all set!</p>
                  <p className="text-sm">We'll notify you when {integration.name} is ready.</p>
                </div>
              </div>
            </div>
          )}

          {/* Call to Action */}
          {integration.status === 'concept' && (
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-1">Help Us Prioritize</h4>
              <p className="text-sm text-purple-700 mb-3">
                Your interest helps us decide which integrations to build next.
              </p>
              <Button 
                size="sm" 
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Vote for This Integration
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 